import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Cliente } from '../../../core/models/clientes/cliente';
import { ClienteLocalEntrega } from '../../../core/models/clientes/cliente-local-entrega';
import { Configuracao } from '../../../core/models/configuracoes/configuracao';
import { FormaPagamento } from '../../../core/models/formas-pagamento/forma-pagamento';
import { Pedido } from '../../../core/models/pedidos/pedido';
import { ProdutoPromocional } from '../../../core/models/produtos/produto-promocional';
import { ProdutoReferenciaCliente } from '../../../core/models/produtos/produto-referencia-cliente';
import { TipoFrete } from '../../../core/models/tipos/tipo-frete';
import { TipoPedido } from '../../../core/models/tipos/tipo-pedido';
import { Transportadora } from '../../../core/models/transportadoras/transportadora';
import { Vendedor } from '../../../core/models/vendedores/vendedor';

/**
 * Bloco de campos do cabeçalho do pedido (Step 0 "Cabeçalho") do wizard de
 * pedidos: Orçamento/Pedido, Canal de Vendas, Vendedor(a), Tipo de Pedido,
 * Transportadora, Frete, Empresa (referência), Forma de Pagamento,
 * Dt. Validade, Pedido Programado e Local de Entrega
 * (pedidos-cadastro.component.html:842-966).
 *
 * Boundary real: começa em html:842 (`<div class="form-row">` que abre o
 * grupo "Orçamento/Pedido") e vai até html:966 (`</div>` que fecha o segundo
 * `form-row`), imediatamente antes do comentário `<!-- Prazo de Pagamento -->`
 * de html:967, que abre o escopo do bloco seguinte (não tocado por esta
 * task).
 *
 * Animação: bloco verificado (`grep -n "@esvanecerDesvanecer\|@desvanecer"`
 * em todo o pedidos-cadastro.component.html) — nenhuma ocorrência nas linhas
 * 842-966 (as ocorrências mais próximas estão nas linhas 716 e 1018, fora
 * do range). Sem dependência de `Animacao` aqui, mesmo precedente da Task 2.
 *
 * `selecionarTransportadoraPreferencial(cd_transportadora?: number)` (ts:4909)
 * foi listado no brief como método "consumido", mas grep-verificado
 * (`grep -n "selecionarTransportadoraPreferencial"` no .html inteiro) como
 * NUNCA chamado a partir do template — apenas internamente (ts:820, dentro
 * do fluxo de carregamento de dados do cliente). Portanto não faz parte da
 * interação deste bloco e não recebeu `@Output` correspondente.
 *
 * Vendedor(a) — condição real de `disabled` (html:876, ts:467-469 para
 * `bloquearAlteracaoVendedor`):
 *   `pedido != null || pedidoAprovarExterno || bloquearAlteracaoVendedor ||
 *   (!is_master && !is_manager && !is_diretor && configuracao != null &&
 *   !configuracao.tmk_seleciona_vendedor)`
 * `bloquearAlteracaoVendedor` é um getter no pai (ts:467): `configuracao !=
 * null && configuracao.usa_pontuacao === true` — recebido aqui como @Input()
 * já calculado pelo pai (fix pós-revisão final: era um getter local
 * duplicado, agora fonte única de verdade no pai). NOTA: o brief menciona
 * `is_master_seller` como um dos flags de gate, mas ele NÃO aparece na
 * condição real (`is_master`, `is_manager`, `is_diretor` apenas,
 * grep-verificado em ts:876/467) — não reproduzido aqui por fidelidade à
 * fonte real, não ao brief.
 *
 * Transportadora — duas variantes mutuamente exclusivas por config
 * (html:894-909): `<select>` quando `configuracao.seleciona_transportadora`
 * é true; `<input readonly>` com `clienteSelecionado.nm_transportadora`
 * quando é false E o cliente tem transportadora preferencial cadastrada.
 * Quando nenhuma das duas condições é satisfeita, nada é exibido (mesmo
 * comportamento do original).
 *
 * Dt. Validade / Pedido Programado — grep-verificado (html:944, 952):
 * ambos usam `[(ngModel)]` (não método próprio) sobre propriedades tipadas
 * `{ year: number, month: number, day: number }` (ts:167, 171) ligadas a
 * `ngbDatepicker`, sem `(dateSelect)`/`(ngModelChange)` explícito no
 * original. Reproduzido aqui como `[ngModel]` (one-way, a partir do
 * @Input()) + `(ngModelChange)` que reemite via `@Output()` — desaçucaramento
 * padrão do Angular para `[(ngModel)]`, sem mutação local do @Input,
 * preservando o fluxo unidirecional já usado pelas Tasks 1-2. `dataValidadeInvalida`
 * (ts:173) é uma propriedade separada (não recalculada aqui), usada apenas
 * para `[ngClass]` e a mensagem de erro (html:945-947).
 *
 * Componente puramente apresentacional: nenhuma ação de negócio é executada
 * aqui, apenas reemitida via @Output() para o pai reagir com os métodos
 * reais (wiring na Task 4).
 */
@Component({
  selector: 'app-pedido-cabecalho-campos',
  standalone: false,
  templateUrl: './pedido-cabecalho-campos.component.html',
  styleUrls: ['./pedido-cabecalho-campos.component.scss']
})
export class PedidoCabecalhoCamposComponent {
  /* Gate comum a quase todos os campos (html:848, 852, 861, 865, 869, 876,
   * 887, 897, 913, 923, 934, 944, 952, 958). */
  @Input() pedidoAprovarExterno = false;

  @Input() clienteSelecionado: Cliente = null;
  @Input() configuracao: Configuracao = null;
  @Input() produtosAdicionados: ProdutoPromocional[] = [];

  /* Orçamento/Pedido (html:844-855) — real: ts:4893/247. */
  @Input() pedidoOrcamentoSelecionado: number = null;

  /* Canal de Vendas (html:856-872) — real: ts:4875/134. */
  @Input() canalVendasSelecionado: number = null;

  /* Vendedor(a) (html:873-881) — real: ts:4937/403/405. Flags de
   * disabled (ts:876): `pedido`, `is_master`, `is_manager`, `is_diretor`,
   * `bloquearAlteracaoVendedor` (agora @Input, ver doc da classe). */
  @Input() vendedores: Vendedor[] = [];
  @Input() vendedorSelecionado: Vendedor = null;
  @Input() pedido: Pedido = null;
  @Input() is_master = false;
  @Input() is_manager = false;
  @Input() is_diretor = false;
  @Input() bloquearAlteracaoVendedor = false;

  /* Tipo de Pedido (html:884-892) — real: ts:4897/389/392. */
  @Input() tiposPedido: TipoPedido[] = [];
  @Input() tiposPedidoSelecionado: TipoPedido = null;

  /* Transportadora (html:893-909) — real: ts:4905/397/399. */
  @Input() transportadoras: Transportadora[] = [];
  @Input() transportadoraSelecionada: Transportadora = null;

  /* Frete (html:910-918) — real: ts:4885/383/385. */
  @Input() tiposFrete: TipoFrete[] = [];
  @Input() tipoFreteSelecionado: TipoFrete = null;

  /* Empresa/referência (html:919-929) — real: ts:4869/364/366. */
  @Input() produtosReferenciasCliente: ProdutoReferenciaCliente[] = null;
  @Input() produtoReferenciaClienteSelecionado: string = "";

  /* Forma de Pagamento (html:930-940) — real: ts:4879/192/194. */
  @Input() formasPagamento: FormaPagamento[] = [];
  @Input() formaPagamentoSelecionada: FormaPagamento = null;

  /* Dt. Validade (html:941-948) — real: ts:171/173. */
  @Input() dataValidade: { year: number, month: number, day: number } = null;
  @Input() dataValidadeInvalida = false;

  /* Pedido Programado (html:949-953) — real: ts:167. */
  @Input() dataPrevisaoEntrega: { year: number, month: number, day: number } = null;

  /* Local de Entrega (html:954-965) — real: ts:4889/144/146. */
  @Input() clienteLocaisEntrega: ClienteLocalEntrega[] = [];
  @Input() clienteLocalEntregaSelecionado: ClienteLocalEntrega = null;

  @Output() pedidoOrcamentoAlterado = new EventEmitter<string>();
  @Output() canalVendaAlterado = new EventEmitter<string>();
  @Output() vendedorAlterado = new EventEmitter<string>();
  @Output() tipoPedidoAlterado = new EventEmitter<string>();
  @Output() transportadoraAlterada = new EventEmitter<string>();
  @Output() freteAlterado = new EventEmitter<string>();
  @Output() referenciaClienteAlterada = new EventEmitter<string>();
  @Output() formaPagamentoAlterada = new EventEmitter<string>();
  @Output() localEntregaAlterado = new EventEmitter<string>();
  /** Espelha `[(ngModel)]="dataValidade"` (html:944) desaçucarado. */
  @Output() dataValidadeAlterada = new EventEmitter<{ year: number, month: number, day: number }>();
  /** Espelha `[(ngModel)]="dataPrevisaoEntrega"` (html:952) desaçucarado. */
  @Output() dataPrevisaoEntregaAlterada = new EventEmitter<{ year: number, month: number, day: number }>();
}
